import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { formatCurrency } from '../../utils/productPricing';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import { 
  processAffiliateLink, 
  getAffiliateCookie, 
  debugAffiliateCookies,
  useAffiliateDetection 
} from '../../utils/affiliateCookies';
import './LandingCheckout.css';

const SecureCheckout = () => {
  const { secureCode } = useParams();
  const [searchParams] = useSearchParams();
  
  // Estados do produto e checkout
  const [product, setProduct] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados do formulário
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    document: ''
  });
  
  const [payment, setPayment] = useState({
    method: 'credit_card',
    installments: 1
  });

  const [creditCard, setCreditCard] = useState({
    number: '',
    holder: '',
    month: '',
    year: '',
    cvc: ''
  });
  
  // Estados de processamento
  const [processing, setProcessing] = useState(false);
  
  // Estados de afiliado com cookies
  const [affiliateConfig, setAffiliateConfig] = useState(null);
  const [affiliateData, setAffiliateData] = useState(null);
  const { checkForAffiliate, clearAffiliate } = useAffiliateDetection();
  
  // Dados do afiliado (legacy - mantido para compatibilidade)
  const affiliateRef = searchParams.get('ref');
  const affiliateId = searchParams.get('affiliate_id');
  const utmSource = searchParams.get('utm_source');

  // Estado para métodos de pagamento do checkout
  const [checkoutMethods, setCheckoutMethods] = useState({
    credit_card: true,
    boleto: true,
    pix: true,
    after_pay: false
  });
  
  // Timer de urgência (condicional)
  const [timeLeft, setTimeLeft] = useState(null);
  const [showTimer, setShowTimer] = useState(false);

  // Carregar dados do produto usando código seguro
  useEffect(() => {
    loadSecureProduct();
  }, [secureCode]);

  // Timer countdown (condicional)
  useEffect(() => {
    if (!showTimer || !timeLeft) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (!prev) return null;
        
        const { hours, minutes, seconds } = prev;
        const totalSeconds = hours * 3600 + minutes * 60 + seconds;
        
        if (totalSeconds <= 1) {
          setShowTimer(false);
          return null;
        }
        
        const newTotal = totalSeconds - 1;
        return {
          hours: Math.floor(newTotal / 3600),
          minutes: Math.floor((newTotal % 3600) / 60),
          seconds: newTotal % 60
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showTimer, timeLeft]);

  const loadSecureProduct = async () => {
    try {
      setLoading(true);
      
      // Buscar produto usando código seguro
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/secure-checkout/${secureCode}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Link inválido ou expirado');
        }
        throw new Error('Erro ao carregar produto');
      }
      
      const result = await response.json();
      
      if (result.success && result.product) {
        console.log('🔍 Produto carregado via código seguro:', {
          secure_code: secureCode,
          product_id: result.product.id,
          product_name: result.product.name,
          checkout_id: result.checkout_id
        });
        
        setProduct(result.product);
        
        // Se há checkout específico, buscar suas configurações
        if (result.checkout_id) {
          try {
            const checkoutResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/products/${result.product.id}/checkouts`);
            if (checkoutResponse.ok) {
              const checkoutData = await checkoutResponse.json();
              if (checkoutData.success && checkoutData.data) {
                const specificCheckout = checkoutData.data.find(c => c.id === result.checkout_id);
                
                if (specificCheckout && specificCheckout.status === 'active') {
                  setCheckout(specificCheckout);
                  
                  // Configurar timer se habilitado
                  if (specificCheckout.use_counter && specificCheckout.counter_config?.enabled) {
                    const timeLimit = specificCheckout.counter_config.time_limit || 3600;
                    const hours = Math.floor(timeLimit / 3600);
                    const minutes = Math.floor((timeLimit % 3600) / 60);
                    const seconds = timeLimit % 60;
                    
                    setTimeLeft({ hours, minutes, seconds });
                    setShowTimer(true);
                  }

                  // Configurar métodos de pagamento
                  let paymentMethods = specificCheckout.payment_methods;
                  if (typeof paymentMethods === 'string') {
                    try {
                      paymentMethods = JSON.parse(paymentMethods);
                    } catch (e) {
                      paymentMethods = {};
                    }
                  }
                  
                  setCheckoutMethods({
                    credit_card: paymentMethods?.credit_card === true,
                    boleto: paymentMethods?.boleto === true,
                    pix: paymentMethods?.pix === true,
                    after_pay: paymentMethods?.after_pay === true
                  });

                  // Se o checkout tem plano específico, filtrar pelo plano correto
                  if (specificCheckout.linked_plan_id && result.product.plans) {
                    const specificPlan = result.product.plans.find(plan => 
                      plan.id === specificCheckout.linked_plan_id
                    );
                    
                    if (specificPlan) {
                      setProduct(prev => ({
                        ...prev,
                        plans: [specificPlan]
                      }));
                      console.log('✅ Usando plano específico do checkout:', specificPlan.name);
                    }
                  }
                }
              }
            }
          } catch (checkoutErr) {
            console.warn('⚠️ Erro ao carregar checkout específico, usando configuração padrão');
          }
        }
        
      } else {
        throw new Error(result.message || 'Produto não encontrado');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processPurchase = async () => {
    try {
      setProcessing(true);

      // Validações
      if (!customer.name || !customer.email || !customer.phone) {
        throw new Error('Preencha todos os dados obrigatórios');
      }

      const saleData = {
        product_id: product.id,
        customer_data: customer,
        affiliate_id: affiliateId || null,
        payment_method: payment.method,
        total_amount: parseFloat(product?.plans?.[0]?.price || 0),
        utm_source: utmSource,
        secure_code: secureCode // Incluir código seguro para rastreamento
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/checkout/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao processar compra');
      }

      // Redirecionar para página de sucesso
      window.location.href = '/#/checkout/success';

    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="landing-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="landing-error">
        <div className="container">
          <div className="error-content">
            <Icon icon="mdi:alert-circle" className="error-icon" />
            <h2>Link inválido</h2>
            <p>{error || 'Este link de checkout não está disponível no momento.'}</p>
            <small className="text-muted">
              Verifique se o link está correto ou entre em contato com o vendedor.
            </small>
          </div>
        </div>
      </div>
    );
  }

  // Usar o mesmo layout do LandingCheckout
  return (
    <div className="landing-checkout">
      {/* Banner personalizado (se configurado) */}
      {checkout?.custom_colors?.banner_config?.enabled && (
        <div className={`checkout-banner ${checkout.custom_colors.banner_config.template}`}>
          <div className="banner-content">
            <h2 style={{ color: checkout.custom_colors.banner_config.text_color }}>
              {checkout.custom_colors.banner_config.title}
            </h2>
            <p style={{ color: checkout.custom_colors.banner_config.text_color }}>
              {checkout.custom_colors.banner_config.subtitle}
            </p>
          </div>
        </div>
      )}

      {/* Timer de urgência */}
      {showTimer && timeLeft && (
        <div className="urgency-timer">
          <div className="container">
            <div className="timer-content">
              <Icon icon="mdi:clock-alert" />
              <span>Oferta expira em:</span>
              <div className="timer-display">
                <span>{String(timeLeft.hours).padStart(2, '0')}</span>:
                <span>{String(timeLeft.minutes).padStart(2, '0')}</span>:
                <span>{String(timeLeft.seconds).padStart(2, '0')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container">
        <div className="row">
          {/* Coluna do Produto */}
          <div className="col-lg-6">
            <div className="product-showcase">
              <div className="product-image">
                {product.image ? (
                  <img 
                    src={`${process.env.REACT_APP_API_URL}/api/images/products/${product.image.split('/').pop()}`} 
                    alt={product.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="no-image">
                    <Icon icon="mdi:image-off" />
                    <span>Sem imagem</span>
                  </div>
                )}
              </div>

              <div className="product-info">
                <span className="product-title">{product.name}</span>
                
                <div className="product-pricing">
                  {product.plans && product.plans.length > 0 && (
                    <>
                      <div className="price-section">
                        <span className="price-label">De:</span>
                        <span className="original-price">{formatCurrency(parseFloat(product.plans[0].original_price || product.plans[0].price))}</span>
                      </div>
                      <div className="price-section main-price">
                        <span className="price-label">Por apenas:</span>
                        <span className="current-price">{formatCurrency(parseFloat(product.plans[0].price))}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Descrição do Produto */}
                <div className="product-description">
                  <span className="section-title">Sobre este produto:</span>
                  <p>{product.description || 'Produto de alta qualidade com excelente custo-benefício.'}</p>
                </div>

                <div className="product-benefits">
                  <span className="section-title">Você está adquirindo:</span>
                  <ul>
                    <li>
                      <Icon icon="mdi:check-circle" />
                      <span>{product.name}</span>
                    </li>
                    {product.plans && product.plans[0] && product.plans[0].included_items_quantity && (
                      <li>
                        <Icon icon="mdi:check-circle" />
                        <span>{product.plans[0].included_items_quantity} itens inclusos</span>
                      </li>
                    )}
                    {product.plans && product.plans[0] && product.plans[0].max_installments > 1 && (
                      <li>
                        <Icon icon="mdi:check-circle" />
                        <span>Parcelamento em até {product.plans[0].max_installments}x</span>
                      </li>
                    )}
                    <li>
                      <Icon icon="mdi:check-circle" />
                      <span>Acesso imediato</span>
                    </li>
                    <li>
                      <Icon icon="mdi:check-circle" />
                      <span>Suporte especializado</span>
                    </li>
                    <li>
                      <Icon icon="mdi:check-circle" />
                      <span>Garantia de 7 dias</span>
                    </li>
                  </ul>
                </div>

                {/* Suporte ao cliente (se configurado) */}
                {(checkout?.support_phone || checkout?.whatsapp_phone) && (
                  <div className="contact-support">
                    <span className="section-title">
                      <Icon icon="mdi:headset" />
                      Suporte ao Cliente
                    </span>
                    <div className="support-buttons">
                      {checkout.support_phone && (
                        <a href={`tel:${checkout.support_phone}`} className="support-btn">
                          <Icon icon="mdi:phone" />
                          {checkout.support_phone}
                        </a>
                      )}
                      {checkout.whatsapp_phone && (
                        <a 
                          href={`https://wa.me/${checkout.whatsapp_phone.replace(/\D/g, '')}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="support-btn whatsapp"
                        >
                          <Icon icon="mdi:whatsapp" />
                          WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Coluna do Formulário */}
          <div className="col-lg-6">
            <div className="checkout-form">
              <div className="form-header">
                <span className="section-title">Complete seus dados</span>
                <p>Preencha as informações abaixo para finalizar sua compra</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); processPurchase(); }}>
                {/* Dados Pessoais */}
                <div className="form-section">
                  <span className="section-title">
                    <Icon icon="mdi:account" />
                    Seus Dados
                  </span>
                  
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={customer.name}
                      onChange={(e) => setCustomer(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Digite seu nome completo"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>E-mail *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={customer.email}
                      onChange={(e) => setCustomer(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="seuemail@exemplo.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>WhatsApp *</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={customer.phone}
                      onChange={(e) => setCustomer(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(11) 99999-9999"
                      required
                    />
                  </div>
                </div>

                {/* Métodos de Pagamento */}
                <div className="form-section">
                  <span className="section-title">
                    <Icon icon="mdi:credit-card" />
                    Forma de Pagamento
                  </span>
                  
                  <div className="payment-methods">
                    {checkoutMethods.pix && (
                      <div className="payment-option">
                        <input
                          type="radio"
                          id="pix"
                          name="payment"
                          value="pix"
                          checked={payment.method === 'pix'}
                          onChange={(e) => setPayment(prev => ({ ...prev, method: e.target.value }))}
                        />
                        <label htmlFor="pix">
                          <Icon icon="mdi:qrcode" />
                          PIX (Aprovação Imediata)
                        </label>
                      </div>
                    )}

                    {checkoutMethods.boleto && (
                      <div className="payment-option">
                        <input
                          type="radio"
                          id="boleto"
                          name="payment"
                          value="boleto"
                          checked={payment.method === 'boleto'}
                          onChange={(e) => setPayment(prev => ({ ...prev, method: e.target.value }))}
                        />
                        <label htmlFor="boleto">
                          <Icon icon="mdi:file-document-outline" />
                          Boleto Bancário
                        </label>
                      </div>
                    )}

                    {checkoutMethods.after_pay && (
                      <div className="payment-option">
                        <input
                          type="radio"
                          id="after_pay"
                          name="payment"
                          value="after_pay"
                          checked={payment.method === 'after_pay'}
                          onChange={(e) => setPayment(prev => ({ ...prev, method: e.target.value }))}
                        />
                        <label htmlFor="after_pay">
                          <Icon icon="mdi:calendar-clock" />
                          Pague Depois (AfterPay)
                        </label>
                      </div>
                    )}

                    {checkoutMethods.credit_card && (
                      <div className="payment-option">
                        <input
                          type="radio"
                          id="credit_card"
                          name="payment"
                          value="credit_card"
                          checked={payment.method === 'credit_card'}
                          onChange={(e) => setPayment(prev => ({ ...prev, method: e.target.value }))}
                        />
                        <label htmlFor="credit_card">
                          <Icon icon="mdi:credit-card" />
                          Cartão de Crédito
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Campos do Cartão de Crédito */}
                  {payment.method === 'credit_card' && (
                    <div className="credit-card-fields">
                      <div className="form-group">
                        <label>Número do Cartão *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={creditCard.number}
                          onChange={(e) => setCreditCard(prev => ({ ...prev, number: e.target.value }))}
                          placeholder="1234 5678 9012 3456"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Nome no Cartão *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={creditCard.holder}
                          onChange={(e) => setCreditCard(prev => ({ ...prev, holder: e.target.value }))}
                          placeholder="Nome como está no cartão"
                          required
                        />
                      </div>

                      <div className="row">
                        <div className="col-4">
                          <div className="form-group">
                            <label>Mês *</label>
                            <select
                              className="form-control"
                              value={creditCard.month}
                              onChange={(e) => setCreditCard(prev => ({ ...prev, month: e.target.value }))}
                              required
                            >
                              <option value="">Mês</option>
                              {Array.from({ length: 12 }, (_, i) => (
                                <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                  {String(i + 1).padStart(2, '0')}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="form-group">
                            <label>Ano *</label>
                            <select
                              className="form-control"
                              value={creditCard.year}
                              onChange={(e) => setCreditCard(prev => ({ ...prev, year: e.target.value }))}
                              required
                            >
                              <option value="">Ano</option>
                              {Array.from({ length: 10 }, (_, i) => {
                                const year = new Date().getFullYear() + i;
                                return (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="form-group">
                            <label>CVV *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={creditCard.cvc}
                              onChange={(e) => setCreditCard(prev => ({ ...prev, cvc: e.target.value }))}
                              placeholder="123"
                              maxLength="4"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botão de Finalizar */}
                <button
                  type="submit"
                  className="btn-finalize"
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:shield-check" />
                      Finalizar Compra Segura
                    </>
                  )}
                </button>

                <div className="security-badges">
                  <small className="text-muted">
                    <Icon icon="mdi:lock" />
                    Checkout 100% seguro e criptografado
                  </small>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecureCheckout;
